import { request } from './core';
import { Order, OrderItem, OrderItemOption } from '../../types';
import type {
  Order as ApiOrder,
  OrderItem as ApiOrderItem,
  OrderItemOptionRecord,
  SubBill as ApiSubBill,
} from '@mosehxl/types';
import { mapOrderItemOptionsToApiPayload } from '../../utils/orderItemOptions';

type RawOrderItem = Partial<ApiOrderItem>;
type RawOrder = Partial<ApiOrder> &
  Omit<Pick<ApiOrder, 'id' | 'total_amount' | 'total_tax' | 'created_at' | 'status' | 'payment_method'>, 'id' | 'total_amount' | 'total_tax' | 'created_at'> & {
    id: string | number;
    total_amount: number | string;
    total_tax: number | string;
    created_at: string | Date;
    waiter_user_id?: number | null;
    waiter_display_name?: string | null;
    table_label?: string | null;
    legal_sequence_number?: number | null;
    kitchen_ticket_day_number?: number | null;
    kitchen_print_targets?: Array<{
      printer_id?: number | null;
      printer_name?: string;
      printer_slug?: string;
      kitchen_ticket_day_number?: number | null;
    }>;
    cashier_user_id?: number | null;
    cashier_display_name?: string | null;
    items?: Array<Partial<ApiOrderItem> & {
      id?: string | number;
      product_id?: string | number | null;
      quantity?: number | string;
      unit_price?: number | string;
      total_price?: number | string;
      tax_rate?: number | string;
      tax_amount?: number | string;
      options?: OrderItemOptionRecord[];
    }>;
    sub_bills?: Array<Partial<ApiSubBill> & {
      id?: string | number;
      amount?: number | string;
      created_at?: string | Date;
    }>;
    change_amount?: number | string | null;
  };

function toNumber(value: unknown, fallback: number = 0): number {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? parseFloat(value)
        : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function mapRawOption(option: OrderItemOptionRecord): OrderItemOption {
  return {
    id: String(option.id),
    groupId: option.group_id != null ? String(option.group_id) : '',
    groupName: option.group_name_snapshot,
    choiceId: option.choice_id != null ? String(option.choice_id) : null,
    choiceLabel: option.choice_label_snapshot,
    freeText: option.free_text,
    displayOrder: option.display_order,
  };
}

function mapRawItem(orderId: string | number, item: RawOrderItem): OrderItem {
  const totalPrice = toNumber(item.total_price);
  const unitPrice = toNumber(item.unit_price);
  const taxRate = toNumber(item.tax_rate, 20) / 100;

  return {
    id: item.id ? String(item.id) : `${orderId}-${item.product_id || 'unknown'}`,
    productId: item.product_id ? String(item.product_id) : 'unknown',
    productName: item.product_name || 'Unknown Product',
    quantity: toNumber(item.quantity, 1),
    unitPrice,
    totalPrice,
    taxRate,
    taxAmount: toNumber(item.tax_amount),
    isHappyHourApplied: Boolean(item.happy_hour_applied),
    isManualHappyHour: Boolean(item.happy_hour_applied && item.is_manual_happy_hour),
    isOffert: totalPrice === 0,
    originalPrice: unitPrice,
    description: item.description || undefined,
    options: (item.options ?? []).map(mapRawOption),
  };
}

function mapRawOrder(order: RawOrder): Order {
  return {
    id: String(order.id),
    items: (order.items || []).map((item) => mapRawItem(order.id, item)),
    totalAmount: toNumber(order.total_amount),
    taxAmount: toNumber(order.total_tax),
    discountAmount: 0,
    finalAmount: toNumber(order.total_amount),
    createdAt: new Date(order.created_at),
    status: order.status,
    paymentMethod: order.payment_method,
    subBills: (order.sub_bills || []).map((subBill) => ({
      id: String(subBill.id),
      orderId: String(order.id),
      paymentMethod: subBill.payment_method as 'cash' | 'card',
      amount: toNumber(subBill.amount),
      status: subBill.status as 'pending' | 'paid',
      createdAt: new Date(subBill.created_at || new Date()),
    })),
    notes: order.notes,
    tips: order.tips || 0,
    change: order.change || 0,
    operationType: order.operation_type as 'sale' | 'change' | undefined,
    changeAmount: order.change_amount != null ? toNumber(order.change_amount) : null,
    waiterUserId: order.waiter_user_id != null ? Number(order.waiter_user_id) : null,
    waiterDisplayName: order.waiter_display_name != null ? String(order.waiter_display_name) : null,
    tableLabel: order.table_label != null ? String(order.table_label) : null,
    legalSequenceNumber:
      order.legal_sequence_number != null ? Number(order.legal_sequence_number) : null,
    kitchenTicketDayNumber:
      order.kitchen_ticket_day_number != null
        ? Number(order.kitchen_ticket_day_number)
        : null,
    kitchenPrintTargets: (order.kitchen_print_targets ?? []).map((p) => ({
      printerId: p.printer_id != null ? Number(p.printer_id) : null,
      printerName: String(p.printer_name ?? p.printer_slug ?? 'Imprimante'),
      printerSlug: String(p.printer_slug ?? ''),
      kitchenTicketDayNumber:
        p.kitchen_ticket_day_number != null ? Number(p.kitchen_ticket_day_number) : null,
    })),
    cashierUserId: order.cashier_user_id != null ? Number(order.cashier_user_id) : null,
    cashierDisplayName:
      order.cashier_display_name != null ? String(order.cashier_display_name) : null,
  };
}

export async function getOrders(): Promise<Order[]> {
  const orders = await request<RawOrder[]>('/orders');
  return orders.map(mapRawOrder);
}

export async function getOrdersPaginated(params: {
  limit?: number;
  offset?: number;
  waiterUserId?: number;
  search?: string;
}): Promise<{ orders: Order[]; total: number }> {
  const query = new URLSearchParams();
  if (params.limit != null) query.set('limit', String(params.limit));
  if (params.offset != null) query.set('offset', String(params.offset));
  if (params.waiterUserId != null) query.set('waiter_user_id', String(params.waiterUserId));
  if (params.search?.trim()) query.set('search', params.search.trim());

  const response = await request<RawOrder[] | { orders?: RawOrder[]; total?: number }>(
    '/orders' + (query.toString() ? `?${query.toString()}` : '')
  );

  // Backend supports both the legacy shape (array) and paginated shape ({orders,total}).
  const rawOrders: RawOrder[] = Array.isArray(response) ? response : response?.orders ?? [];
  const total: number = Array.isArray(response) ? rawOrders.length : response?.total ?? rawOrders.length;
  const orders = rawOrders.map(mapRawOrder);

  return { orders, total };
}

export async function createOrder(order: {
  items: OrderItem[];
  totalAmount: number;
  taxAmount: number;
  paymentMethod: 'cash' | 'card' | 'split';
  status?: string;
  notes?: string;
  tips?: number;
  change?: number;
  sub_bills?: Array<{ payment_method: 'cash' | 'card'; amount: number }>;
  waiter_user_id?: number | null;
  waiter_display_name?: string | null;
  table_label?: string | null;
  pinActorToken?: string;
}): Promise<Order> {
  // Accounting: send exact amounts for storage (no rounding). Taxes are derived from
  // prices (taxAmount = totalPrice * taxRate / (1+taxRate)) and may be non-terminating;
  // rounding is for display only to avoid cumulative drift in closures/audits.
  const result = await request<RawOrder>('/orders', {
    method: 'POST',
    headers: order.pinActorToken
      ? { 'x-pin-actor-token': order.pinActorToken }
      : undefined,
    body: JSON.stringify({
      total_amount: order.totalAmount,
      total_tax: order.taxAmount,
      payment_method: order.paymentMethod,
      status: order.status || 'completed',
      notes: order.notes,
      tips: order.tips || 0,
      change: order.change ?? 0,
      waiter_user_id: order.waiter_user_id ?? null,
      waiter_display_name: order.waiter_display_name ?? null,
      table_label: order.table_label ?? null,
      items: order.items.map(item => ({
        product_id: item.productId ? (isNaN(parseInt(String(item.productId))) ? null : parseInt(String(item.productId))) : null,
        product_name: item.productName,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.totalPrice,
        tax_rate: item.taxRate,
        tax_amount: item.taxAmount ?? (item.totalPrice * item.taxRate) / (1 + item.taxRate),
        happy_hour_applied: item.isHappyHourApplied,
        happy_hour_discount_amount: item.isHappyHourApplied ? (item.originalPrice ? (item.originalPrice - item.unitPrice) * item.quantity : 0) : 0,
        is_manual_happy_hour:
          item.isHappyHourApplied === true && item.isManualHappyHour === true,
        description: item.description || null,
        options: mapOrderItemOptionsToApiPayload(item.options),
      })),
      ...(order.sub_bills ? { sub_bills: order.sub_bills } : {}),
    }),
  });

  return {
    id: result.id.toString(),
    items: order.items,
    totalAmount: toNumber(result.total_amount),
    taxAmount: toNumber(result.total_tax),
    discountAmount: 0,
    finalAmount: toNumber(result.total_amount),
    createdAt: new Date(result.created_at),
    status: result.status,
    paymentMethod: order.paymentMethod,
    subBills: (result.sub_bills || []).map((subBill) => ({
      id: String(subBill.id),
      orderId: String(result.id),
      paymentMethod: subBill.payment_method as 'cash' | 'card',
      amount: toNumber(subBill.amount),
      status: subBill.status as 'pending' | 'paid',
      createdAt: new Date(subBill.created_at || new Date()),
    })),
  };
}

export interface OrderAuditActor {
  user_id: string | null;
  display_name: string | null;
  action_type: string;
  timestamp: string;
}

export async function getOrderAudit(orderId: number): Promise<OrderAuditActor[]> {
  const response = await request<{
    audit_entries: OrderAuditActor[];
  }>(`/orders/audit/${orderId}`);
  return response.audit_entries ?? [];
}

